import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  SectorProps,
} from 'recharts'
import { KeyValuePair } from './TokenDetails'
import { formatNumericValue } from 'utils/numbers'
import { useTokenGenesis } from 'hooks/useTokenGenesis'
import { useMemo, useState } from 'react'
import { TopHolder, useTop20Holders } from 'hooks/useTop20Holders'
import SheenLoader from '../loading/SheenLoader'
import { useMintInfo } from 'hooks/useMintInfo'
import { useBirdeyeTokenOverview } from 'hooks/useBirdeyeTokenOverview'
import { useTranslation } from 'next-i18next'
import store from 'stores/store'

type ChartData = {
  name: string
  value: number
}

const TokenDistribution = () => {
  const { t } = useTranslation('token-page')
  const { data: tokenOverview } = useBirdeyeTokenOverview()
  const { holder } = tokenOverview ?? {}
  const { creatorBalance, creationData } = useTokenGenesis()
  const baseToken = store((s) => s.market.baseToken)
  const { mintInfoData } = useMintInfo(baseToken.mint, baseToken.token_program)
  const { data: topHolders, isInitialLoading: loadingTopHolders } =
    useTop20Holders(baseToken.mint)
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined)

  const supply = useMemo(() => {
    if (!mintInfoData?.mintInfo?.supply) return 0
    return mintInfoData.mintInfo.supply
  }, [mintInfoData])

  const top10Percent = useMemo(() => {
    if (!topHolders || !topHolders?.length || !supply) return 0
    const top10Balance = topHolders
      .slice(0, 10)
      .reduce((a: number, c: TopHolder) => {
        return a + c.uiAmount
      }, 0)
    const percentage = (top10Balance / supply) * 100
    return percentage
  }, [topHolders, supply])

  const creatorPercent = useMemo(() => {
    if (!creatorBalance || !supply) return 0
    return (creatorBalance / supply) * 100
  }, [creatorBalance, supply])

  const creatorInTop10 = useMemo(() => {
    if (!creationData?.owner || !topHolders?.length) return 0
    const inTop10 = topHolders
      .slice(0, 10)
      .find((holder) => holder.address.toString() === creationData.owner)
    return !!inTop10
  }, [creationData, topHolders])

  const smallHoldersPercent = useMemo(() => {
    const creatorCont = creatorInTop10 ? 0 : creatorPercent
    return 100 - top10Percent - creatorCont
  }, [top10Percent, creatorPercent, creatorInTop10])

  const chartData = useMemo(() => {
    return [
      { name: t('distribution.creator'), value: creatorPercent },
      { name: t('distribution.top10'), value: top10Percent },
      { name: t('distribution.rest'), value: smallHoldersPercent },
    ]
  }, [top10Percent, creatorPercent, smallHoldersPercent, t])

  const handleMouseEnter = (data: ChartData, index: number) => {
    setActiveIndex(index)
  }

  const handleMouseLeave = () => {
    setActiveIndex(undefined)
  }

  const renderActiveShape = ({
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
  }: SectorProps) => {
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius! + 4}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    )
  }

  const [heroTitle, heroValue] = useMemo(() => {
    if (!chartData.length) return [undefined, undefined]
    if (activeIndex === undefined) {
      return [t('distribution.holders'), holder]
    } else {
      const segment = chartData[activeIndex]
      return [segment.name, segment.value]
    }
  }, [activeIndex, chartData, holder, t])

  return (
    <div className={`shared-container h-full p-4 md:p-6`}>
      <h3 className="mb-4">{t('distribution.title')}</h3>
      <div className="flex flex-col items-center pb-4">
        <div className="relative size-[210px]">
          <ResponsiveContainer height="100%" width="100%">
            <PieChart width={210} height={210}>
              <Pie
                cursor="pointer"
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                minAngle={2}
                startAngle={90}
                endAngle={450}
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {chartData.map((entry, index) => {
                  const fillColor = index % 2 ? '#591D87' : '#843C77'

                  const opacity = 1 - index * 0.2
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={fillColor}
                      opacity={opacity}
                      stroke="none"
                    />
                  )
                })}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {heroValue != null ? (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
              <p className="text-xs leading-none sm:text-sm">{heroTitle}</p>
              <span className="font-mono text-base leading-none text-th-fgd-1 sm:text-xl">
                {activeIndex == null
                  ? formatNumericValue(heroValue)
                  : `${heroValue.toFixed(1)}%`}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      <div className="w-full space-y-3">
        <KeyValuePair
          title={t('distribution.creator')}
          value={creatorPercent ? `${creatorPercent.toFixed(1)}%` : '0%'}
          isNumber
        />
        <KeyValuePair
          title={t('distribution.top10')}
          value={
            loadingTopHolders ? (
              <SheenLoader>
                <div className="h-4 w-12 bg-th-bkg-2" />
              </SheenLoader>
            ) : top10Percent ? (
              `${top10Percent.toFixed(1)}%`
            ) : (
              '–'
            )
          }
          isNumber
        />
        <KeyValuePair
          title={t('distribution.rest')}
          value={
            smallHoldersPercent ? `${smallHoldersPercent.toFixed(1)}%` : '–'
          }
          isNumber
        />
      </div>
    </div>
  )
}

export default TokenDistribution
